import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { eur, STATUS_LABELS, STATUS_COLORS, fmtDate, PAYMENT_LABELS } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CheckCircle2, XCircle, ChefHat, Package, Bike, Phone, MapPin, Printer, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { BaseMap, Marker, CUSTOMER_ICON } from '@/components/MapBase';
import { printOrder } from '@/lib/print';
import { detectNewOrders, playOrderChime, showOrderNotification } from '@/lib/notifications';

const COLUMNS = [
  { key: 'pending', label: 'Nuovi' },
  { key: 'accepted', label: 'Accettati' },
  { key: 'preparing', label: 'In preparazione' },
  { key: 'ready', label: 'Pronti' },
  { key: 'in_delivery', label: 'In consegna' },
  { key: 'delivered', label: 'Consegnati' },
  { key: 'rejected', label: 'Rifiutati' },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [newSlot, setNewSlot] = useState('');
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const deleteOrder = async () => {
    if (!orderToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${orderToDelete.id}`);
      toast.success(`Ordine #${orderToDelete.order_number} eliminato`);
      setOrders(prev => prev.filter(o => o.id !== orderToDelete.id));
      if (selected?.id === orderToDelete.id) setSelected(null);
      setOrderToDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione ordine');
    } finally {
      setDeleting(false);
    }
  };

  const load = () => {
    api.get('/admin/orders').then(r => {
      const list = r.data || [];
      setOrders(list);
      // Detect new orders (only "pending" trigger the chime/notification)
      const newOnes = detectNewOrders(list).filter(o => o.status === 'pending');
      if (newOnes.length > 0) {
        playOrderChime();
        newOnes.forEach(o => {
          showOrderNotification(o);
          toast.success(`🛒 Nuovo ordine #${o.order_number} — ${o.customer_name}`, {
            duration: 8000,
          });
          // Auto-print if enabled in settings
          if (settingsRef.current?.printer_enabled && settingsRef.current?.printer_auto_print) {
            try { printOrder(o); } catch (e) {}
          }
        });
      }
    }).catch(() => {});
    api.get('/admin/riders').then(r => setRiders(r.data)).catch(() => {});
  };

  // Load restaurant settings (once) for printer auto-print flag
  const [settings, setSettings] = useState(null);
  const settingsRef = React.useRef(null);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);

  const updateStatus = async (orderId, status) => {
    try {
      const r = await api.patch(`/admin/orders/${orderId}/status`, { status });
      toast.success(`Stato aggiornato: ${STATUS_LABELS[status]}`);
      setOrders(prev => prev.map(o => o.id === orderId ? r.data : o));
      if (selected?.id === orderId) setSelected(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const assignRider = async (orderId, riderId) => {
    try {
      const r = await api.patch(`/admin/orders/${orderId}/assign`, { rider_id: riderId });
      toast.success('Rider assegnato');
      setOrders(prev => prev.map(o => o.id === orderId ? r.data : o));
      if (selected?.id === orderId) setSelected(r.data);
    } catch (e) { toast.error('Errore assegnazione'); }
  };

  const openReschedule = async () => {
    try {
      const s = await api.get('/schedule/status');
      const hours = s.data.hours || [];
      const now = new Date();
      const slots = [];
      const dayLabels = ['Domenica','Lunedì','Martedì','Mercoledì','Giovedì','Venerdì','Sabato'];
      for (let offset = 0; offset < 7; offset++) {
        const d = new Date(now); d.setDate(d.getDate() + offset);
        const backendDay = (d.getDay() + 6) % 7;
        const cfg = hours.find(h => h.day === backendDay);
        if (!cfg?.open) continue;
        for (const sl of cfg.slots || []) {
          const [fh, fm] = (sl.from || '00:00').split(':').map(Number);
          const [th, tm] = (sl.to || '00:00').split(':').map(Number);
          const start = new Date(d); start.setHours(fh, fm, 0, 0);
          const end = new Date(d); end.setHours(th, tm, 0, 0);
          const min = new Date(now.getTime() + 15 * 60000);
          const iter = new Date(Math.max(start.getTime(), Math.ceil(min.getTime() / (15*60000)) * (15*60000)));
          while (iter <= end) {
            if (iter >= start) {
              slots.push({
                iso: iter.toISOString(),
                label: `${offset === 0 ? 'Oggi' : offset === 1 ? 'Domani' : dayLabels[iter.getDay()]} ${String(iter.getDate()).padStart(2,'0')}/${String(iter.getMonth()+1).padStart(2,'0')} — ${String(iter.getHours()).padStart(2,'0')}:${String(iter.getMinutes()).padStart(2,'0')}`,
              });
            }
            iter.setMinutes(iter.getMinutes() + 15);
          }
        }
      }
      setRescheduleSlots(slots.slice(0, 80));
      setNewSlot('');
      setRescheduleOpen(true);
    } catch (e) { toast.error('Errore caricamento slot'); }
  };

  const confirmReschedule = async () => {
    if (!newSlot) return toast.error('Seleziona uno slot');
    try {
      const r = await api.patch(`/admin/orders/${selected.id}/reschedule`, { scheduled_for: newSlot });
      toast.success('Orario aggiornato');
      setOrders(prev => prev.map(o => o.id === r.data.id ? r.data : o));
      setSelected(r.data);
      setRescheduleOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore aggiornamento orario'); }
  };

  return (
    <AdminLayout testid="admin-orders-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Ordini</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Gestione ordini</h1>
        </div>
      </div>

      <div className="overflow-x-auto hide-scrollbar">
        <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(280px, 1fr))` }} data-testid="admin-orders-kanban">
          {COLUMNS.map(col => {
            const list = orders.filter(o => o.status === col.key);
            return (
              <div key={col.key} className="bg-[--brand-surface-2] rounded-2xl p-2 flex flex-col min-h-[400px]">
                <div className="sticky top-0 bg-[--brand-surface-2] z-10 px-2 py-1 flex items-center justify-between">
                  <div className="font-display font-bold text-sm">{col.label}</div>
                  <Badge className="bg-white text-[--brand-text] border-[--brand-border]">{list.length}</Badge>
                </div>
                <div className="space-y-2 mt-2">
                  {list.map(o => (
                    <div key={o.id} className="relative group">
                      <button onClick={() => setSelected(o)}
                        className="w-full text-left rounded-xl bg-white border border-[--brand-border] p-3 pr-10 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition"
                        data-testid="admin-order-card"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">#{o.order_number}</div>
                          <div className="tabular-nums font-semibold text-[--brand-primary]">{eur(o.total)}</div>
                        </div>
                        <div className="text-xs text-[--brand-muted]">{o.customer_name}</div>
                        <div className="text-xs text-[--brand-muted] line-clamp-1">{o.delivery_address}</div>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span className="text-[--brand-muted]">{o.items.length} art.</span>
                          <span className="text-[--brand-muted]">{fmtDate(o.created_at)}</span>
                        </div>
                        {o.assigned_rider_name && (
                          <div className="mt-1 text-xs flex items-center gap-1 text-[--brand-accent]"><Bike size={12}/> {o.assigned_rider_name}</div>
                        )}
                        {o.scheduled_for && (
                          <div className="mt-1 text-[10px] inline-flex items-center gap-1 bg-[#FFF6E1] text-[#8A5A00] px-1.5 py-0.5 rounded-full border border-[#F4D89A]">
                            ⏰ Pre-ordine {new Date(o.scheduled_for).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        aria-label={`Elimina ordine #${o.order_number}`}
                        onClick={(e) => { e.stopPropagation(); setOrderToDelete(o); }}
                        className="absolute top-2 right-2 p-1.5 rounded-lg text-[--brand-muted] hover:text-white hover:bg-[--brand-danger] transition"
                        data-testid={`admin-order-card-delete-${o.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="admin-order-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>Ordine #{selected.order_number}</SheetTitle>
                <SheetDescription>{fmtDate(selected.created_at)}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <Badge className={`${STATUS_COLORS[selected.status]} border rounded-full`}>{STATUS_LABELS[selected.status]}</Badge>

                {/* Quick actions: Print + Reschedule (always visible) */}
                <div className="flex gap-2">
                  <Button variant="secondary" className="flex-1 rounded-xl" onClick={() => printOrder(selected)} data-testid="admin-print-order-button">
                    <Printer size={14} className="mr-1"/>Stampa comanda
                  </Button>
                  {selected.scheduled_for && ['pending','accepted','preparing'].includes(selected.status) && (
                    <Button variant="secondary" className="flex-1 rounded-xl" onClick={openReschedule} data-testid="admin-reschedule-button">
                      <Clock size={14} className="mr-1"/>Sposta orario
                    </Button>
                  )}
                  {!selected.scheduled_for && ['pending','accepted','preparing'].includes(selected.status) && (
                    <Button variant="secondary" className="flex-1 rounded-xl" onClick={openReschedule} data-testid="admin-reschedule-button">
                      <Clock size={14} className="mr-1"/>Schedula
                    </Button>
                  )}
                </div>

                {selected.scheduled_for && (
                  <div className="rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-3 py-2 text-sm text-[#8A5A00] flex items-center gap-2">
                    ⏰ <span>Pre-ordine schedulato: <b>{new Date(selected.scheduled_for).toLocaleString('it-IT', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</b></span>
                  </div>
                )}

                <div className="rounded-xl bg-[--brand-surface-2] p-3 text-sm">
                  <div className="font-semibold">{selected.customer_name}</div>
                  <div className="text-[--brand-muted] flex items-center gap-1 mt-1"><Phone size={12}/> <a href={`tel:${selected.customer_phone}`}>{selected.customer_phone}</a></div>
                  <div className="text-[--brand-muted] flex items-start gap-1 mt-1"><MapPin size={12} className="mt-0.5"/> {selected.delivery_address}</div>
                  {selected.delivery_notes && <div className="mt-2 text-xs italic">Note: {selected.delivery_notes}</div>}
                </div>

                <div className="h-[180px]">
                  <BaseMap center={[selected.delivery_lat, selected.delivery_lng]} zoom={14} style={{ height: '100%' }}>
                    <Marker position={[selected.delivery_lat, selected.delivery_lng]} icon={CUSTOMER_ICON} />
                  </BaseMap>
                </div>

                <div className="space-y-1 text-sm">
                  {selected.items.map((it, i) => (
                    <div key={i} className="rounded-lg bg-white border border-[--brand-border] p-2">
                      <div className="flex justify-between font-semibold"><span>{it.quantity}× {it.name}</span><span className="tabular-nums">{eur(it.unit_price * it.quantity)}</span></div>
                      {it.components_summary?.length > 0 && (
                        <ul className="text-xs text-[--brand-muted] mt-1">
                          {it.components_summary.map((c, k) => <li key={k}>• {c}</li>)}
                        </ul>
                      )}
                      {it.notes && <div className="text-xs italic text-[--brand-muted]">Note: {it.notes}</div>}
                    </div>
                  ))}
                </div>

                <div className="text-sm space-y-1">
                  <Row label="Subtotale" value={eur(selected.subtotal)}/>
                  <Row label="Consegna" value={eur(selected.delivery_fee)}/>
                  <div className="flex justify-between font-bold pt-1"><span>Totale</span><span className="tabular-nums text-[--brand-primary]">{eur(selected.total)}</span></div>
                  <div className="text-xs text-[--brand-muted]">Pagamento: {PAYMENT_LABELS[selected.payment_method]}</div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase text-[--brand-muted]">Azioni</div>
                  {selected.status === 'pending' && (
                    <div className="grid grid-cols-2 gap-2">
                      <Button onClick={() => updateStatus(selected.id, 'accepted')} className="rounded-xl bg-[--brand-primary]" data-testid="admin-accept-order-button"><CheckCircle2 size={14} className="mr-1"/>Accetta</Button>
                      <Button onClick={() => updateStatus(selected.id, 'rejected')} variant="destructive" className="rounded-xl" data-testid="admin-reject-order-button"><XCircle size={14} className="mr-1"/>Rifiuta</Button>
                    </div>
                  )}
                  {selected.status === 'accepted' && (
                    <Button onClick={() => updateStatus(selected.id, 'preparing')} className="w-full rounded-xl bg-[--brand-primary]"><ChefHat size={14} className="mr-1"/>Metti in preparazione</Button>
                  )}
                  {selected.status === 'preparing' && (
                    <Button onClick={() => updateStatus(selected.id, 'ready')} className="w-full rounded-xl bg-[--brand-primary]"><Package size={14} className="mr-1"/>Segna come pronto</Button>
                  )}
                  {(selected.status === 'ready' || selected.status === 'preparing') && (
                    <div>
                      <div className="text-xs text-[--brand-muted] mb-1">Assegna rider</div>
                      <Select value={selected.assigned_rider_id || ''} onValueChange={(v) => assignRider(selected.id, v)}>
                        <SelectTrigger className="rounded-xl" data-testid="admin-assign-rider-select"><SelectValue placeholder="Seleziona rider"/></SelectTrigger>
                        <SelectContent>
                          {riders.filter(r => r.active).map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selected.status === 'ready' && selected.assigned_rider_id && (
                    <Button onClick={() => updateStatus(selected.id, 'in_delivery')} className="w-full rounded-xl bg-[--brand-accent]"><Bike size={14} className="mr-1"/>Segna in consegna</Button>
                  )}
                  {selected.status === 'in_delivery' && (
                    <Button onClick={() => updateStatus(selected.id, 'delivered')} className="w-full rounded-xl bg-[--brand-primary]">Segna come consegnato</Button>
                  )}

                  {/* Danger zone — delete order */}
                  <div className="pt-3 border-t border-[--brand-border] mt-3">
                    <div className="text-xs font-semibold uppercase text-[--brand-danger] mb-2">Zona pericolosa</div>
                    <Button
                      variant="destructive"
                      className="w-full rounded-xl"
                      onClick={() => setOrderToDelete(selected)}
                      data-testid="admin-delete-order-button">
                      <Trash2 size={14} className="mr-2"/>Elimina definitivamente
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Sposta orario di consegna</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[--brand-muted]">
              Ordine <b>#{selected?.order_number}</b> — {selected?.customer_name}
            </div>
            {selected?.scheduled_for && (
              <div className="text-xs text-[--brand-muted]">
                Attuale: {new Date(selected.scheduled_for).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <div>
              <label className="text-sm font-semibold">Nuovo slot</label>
              <Select value={newSlot} onValueChange={setNewSlot}>
                <SelectTrigger className="rounded-xl" data-testid="admin-reschedule-slot-select">
                  <SelectValue placeholder="Seleziona un nuovo orario..." />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {rescheduleSlots.length === 0 && <div className="px-3 py-2 text-sm text-[--brand-muted]">Nessuno slot disponibile</div>}
                  {rescheduleSlots.map(s => <SelectItem key={s.iso} value={s.iso}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-[--brand-muted]">
              Il cliente riceverà una email di aggiornamento con il nuovo orario.
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRescheduleOpen(false)}>Annulla</Button>
            <Button onClick={confirmReschedule} className="bg-[--brand-primary]" data-testid="admin-reschedule-confirm-button">
              <Clock size={14} className="mr-1"/>Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!orderToDelete} onOpenChange={(o) => !o && !deleting && setOrderToDelete(null)}>
        <AlertDialogContent data-testid="admin-delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'ordine #{orderToDelete?.order_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è definitiva. L'ordine di <b>{orderToDelete?.customer_name}</b>
              {orderToDelete?.total ? ` (${eur(orderToDelete.total)})` : ''} verrà rimosso e non potrà essere recuperato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-delete-order-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteOrder(); }}
              disabled={deleting}
              className="bg-[--brand-danger] hover:bg-[--brand-danger]/90 focus:ring-[--brand-danger]"
              data-testid="admin-delete-order-confirm"
            >
              {deleting ? 'Eliminazione…' : 'Elimina definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-[--brand-muted]">{label}</span><span className="tabular-nums">{value}</span></div>;
}
